'use strict';

class _User {
  _id = '';
  name = '';
  email = '';
  role = '';
  $promise = undefined;
}

export function AuthService($location, $http, $cookies, $q, Config, Util, User) {
  'ngInject';

  var safeCb = Util.safeCb;
  var currentUser = new _User();
  var userRoles = Config.getPlatformConfig().getUserRoles();
  /**
   * Check if userRole is >= role
   * @param {String} userRole - role of current user
   * @param {String} role - role to check against
   */
  var hasRole = function(userRoles, requiredRole) {
    var userHasRole = false;

    if (Array.isArray(userRoles)) {
      userHasRole = (userRoles.indexOf(requiredRole) !== -1);
    } else {
      userHasRole = (userRoles === requiredRole);
    }
    return userHasRole;
  };

  if($cookies.get('token') && $location.path() !== '/logout') {
    currentUser = User.get();
  }

  var Auth = {
    /**
     * Authenticate user and save token
     *
     * @param  {Object}   user     - login info
     * @param  {Function} callback - function(error, user)
     * @return {Promise}
     */
    login({
      email,
      password
    }, callback) {
      return $http.post('/auth/local', {
        email,
        password
      })
        .then(res => {
          $cookies.put('token', res.data.token);
          currentUser = User.get();
          return currentUser.$promise;
        })
        .then(user => {
          safeCb(callback)(null, user);
          return user;
        })
        .catch(err => {
          Auth.logout();
          safeCb(callback)(err.data);
          return $q.reject(err.data);
        });
    },

    /**
     * Delete access token and user info
     */
    logout() {
      $cookies.remove('token');
      currentUser = new _User();
    },

    /**
     * Create a new user
     *
     * @param  {Object}   user     - user info
     * @param  {Function} callback - function(error, user)
     * @return {Promise}
     */
    createUser(user, callback) {
      return User.save(user, function(data) {
        $cookies.put('token', data.token);
        currentUser = User.get();
        return safeCb(callback)(null, user);
      }, function(err) {
        Auth.logout();
        return safeCb(callback)(err);
      })
        .$promise;
    },

    /**
     * Change password
     *
     * @param  {String}   oldPassword
     * @param  {String}   newPassword
     * @param  {Function} callback    - function(error, user)
     * @return {Promise}
     */
    changePassword(oldPassword, newPassword, callback) {
      return User.changePassword({
        id: currentUser._id
      }, {
        oldPassword,
        newPassword
      }, function() {
        return safeCb(callback)(null);
      }, function(err) {
        return safeCb(callback)(err);
      })
        .$promise;
    },

    /**
     * Forgot password
     *
     * @param  {String}   email
     * @param  {Function} callback    - function(error, user)
     * @return {Promise}
     */
    forgotPassword(email, callback) {
      return User.forgotPassword({}, {
        email
      }, function() {
        return safeCb(callback)(null);
      }, function(err) {
        return safeCb(callback)(err);
      })
        .$promise;
    },

    /**
     * Reset password
     *
     * @param  {String}   id
     * @param  {String}   token
     * @param  {String}   newPassword
     * @param  {Function} callback    - function(error, user)
     * @return {Promise}
     */
    resetPassword(id, token, newPassword, callback) {
      return User.resetPassword({
        id: id
      }, {
        token, newPassword
      }, function() {
        return safeCb(callback)(null);
      }, function(err) {
        return safeCb(callback)(err);
      })
        .$promise;
    },

    /**
     * Forgot password
     *
     * @param  {String}   id
     * @param  {String}   email
     * @param  {Function} callback    - function(error, user)
     * @return {Promise}
     */
    changeEmail(id, email, callback) {
      return User.changeEmail({id}, {
        email
      }, function() {
        return safeCb(callback)(null);
      }, function(err) {
        return safeCb(callback)(err);
      })
        .$promise;
    },

    updateResidencies(id, residencies, callback) {
      return User.updateResidencies({
        id: id
      },
        residencies,
        function(resIds) {
        return safeCb(callback)(resIds);
      }, function(err) {
        return safeCb(callback)(err);
      })
        .$promise;
    },

    getResidencies(id, callback) {
      return User.getResidencies({
          id: id
        },
        function() {
          return safeCb(callback)(null);
        }, function(err) {
          return safeCb(callback)(err);
        })
        .$promise;
    },

    /**
     * Gets all available info on a user
     *
     * @param  {Function} [callback] - function(user)
     * @return {Promise}
     */
    getCurrentUser(callback) {
      var value = _.get(currentUser, '$promise') ? currentUser.$promise : currentUser;

      return $q.when(value)
        .then(user => {
          safeCb(callback)(user);
          return user;
        }, () => {
          safeCb(callback)({});
          return {};
        });
    },

    /**
     * @name setCurrentUser
     * @description
     *
     *    Sets the current user object
     *
     * @param {user} the user object to replace the current user
     */
    setCurrentUser(user) {
      if (typeof user == 'undefined') return;
      currentUser = user;
    },

    /**
     * Gets all available info on a user
     *
     * @return {Object}
     */
    getCurrentUserSync() {
      return currentUser;
    },

    /**
     * Check if a user is logged in
     *
     * @param  {Function} [callback] - function(is)
     * @return {Promise}
     */
    isLoggedIn(callback) {
      return Auth.getCurrentUser(undefined)
        .then(user => {
          let is = _.get(user, 'role');

          safeCb(callback)(is);
          return is;
        });
    },

    /**
     * Check if a user is logged in
     *
     * @return {Bool}
     */
    isLoggedInSync() {
      return !!_.get(currentUser, 'role');
    },

    /**
     * Check if a user has a specified role or higher
     *
     * @param  {String}     role     - the role to check against
     * @param  {Function} [callback] - function(has)
     * @return {Promise}
     */
    hasRole(role, callback) {
      return Auth.getCurrentUser(undefined)
        .then(user => {
          let has = hasRole(_.get(user, 'role'), role);

          safeCb(callback)(has);
          return has;
        });
    },

    /**
     * Check if a user has a specified role or higher
     *
     * @param  {String} role - the role to check against
     * @return {Bool}
     */
    hasRoleSync(role) {
      return hasRole(_.get(currentUser, 'role'), role);
    },

    /**
     * Check if a user is an admin
     *   (synchronous|asynchronous)
     *
     * @param  {Function|*} callback - optional, function(is)
     * @return {Bool|Promise}
     */
    isAdmin() {
      return Auth.hasRole(...[].concat.apply(['admin'], arguments));
    },

    /**
     * Check if a user is an admin
     *
     * @return {Bool}
     */
    isAdminSync() {
      return Auth.hasRoleSync('admin');
    },

    /**
     * Check if a user is an super admin
     *
     * @return {Bool}
     */
    isSuperAdminSync() {
      return Auth.hasRoleSync('superadmin');
    },

    /**
     * Get auth token
     *
     * @return {String} - a token string used for authenticating
     */
    getToken() {
      return $cookies.get('token');
    },

    /**
     * Update user
     *
     * @param  {Object}   user
     * @param  {Function} callback    - function(error, user)
     * @return {Promise}
     */
    updateUser(user, callback) {
      return User.update({
        id: currentUser._id
      }, user, function() {
        return safeCb(callback)(null);
      }, function(err) {
        return safeCb(callback)(err);
      })
        .$promise;
    },

    /**
     * Patch user roles
     *
     * @param  {Object}   user
     * @param  {Function} callback    - function(error, user)
     * @return {Promise}
     */
    patchUserRoles(user, callback) {
        return User.update({
            id: user._id
        }, {
          role: user.role
        }, function() {
            return safeCb(callback)(null);
        }, function(err) {
            return safeCb(callback)(err);
        })
            .$promise;
    }

  };

  return Auth;
}



// WEBPACK FOOTER //
// ./client/~ap/auth/auth.service.js